import * as React from 'react';
import { Component } from 'react';
import { NOT_AUTHORIZED_STATUS_CODE } from '../../../consts/common';
import { Button } from '../../../components/Button/Button';
import { verifyEmail, verifyPhone } from '../../../services/customer/confirm';
import { checkCustomerSession } from '../../../services/customer/customer';
import { AppCustomer } from '../AppOrders';
import { History, Location } from 'history';
import { Loader } from '../../../components/Loader/Loader';
import { SimpleModal } from '../../../components/SimpleModal/SimpleModal';
import { EmailChangeForm } from './EmailChangeForm/EmailChangeForm';
import { PhoneChangeForm } from './PhoneChangeForm/PhoneChangeForm';
import {
  changeEmail,
  changePhone,
  resendEmailCode,
  resendPhoneCode,
  getCustomerProfile,
} from '../../../services/customer/customer';
import { getPhoneWithoutFirstZero } from '../../../helpers/phone';

interface Props {
  customer: AppCustomer;
  history: History;
  location: Location;
  isRegistrationFromOneTimeCode: boolean;
  updateIsCustomerVerified: () => void;
  onLogoutClick: () => void;
}

interface State {
  emailToken: string;
  phoneToken: string;
  parentCode: string;
  isPhoneVerified: boolean;
  isEmailVerified: boolean;
  isEmailChangeModalOpen: boolean;
  isPhoneChangeModalOpen: boolean;
  isInfoModalOpen: boolean;
  infoModalTitle: string;
  infoModalBody: string;
  isLoading: boolean;
  emailRetrySec?: number;
  phoneRetrySec?: number;
}

export class Verification extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      emailToken: '',
      phoneToken: '',
      isPhoneVerified: undefined,
      isEmailVerified: undefined,
      isEmailChangeModalOpen: false,
      isPhoneChangeModalOpen: false,
      isInfoModalOpen: false,
      infoModalTitle: 'Info',
      infoModalBody: '',
      isLoading: false,
      parentCode: '',
      emailRetrySec: undefined,
      phoneRetrySec: undefined,
    };
  }

  componentDidMount() {
    const { customer, onLogoutClick } = this.props;

    const { sessionKey } = customer;
    checkCustomerSession(sessionKey).then(status => {
      if (status === NOT_AUTHORIZED_STATUS_CODE) {
        onLogoutClick();
        this.props.history.push('/login');
      }
    });

    this.setState({
      isLoading: true,
    });

    getCustomerProfile(customer).then(profile => {
      const { verification, parentCode } = profile;
      const { status } = verification;
      const { sms, email } = status;
      switch (true) {
        case sms && email:
          this.setState({
            isPhoneVerified: sms,
            isEmailVerified: email,
            isLoading: false,
            parentCode: parentCode,
          });
          break;
        case sms:
          this.setState({
            isPhoneVerified: sms,
            isLoading: false,
            parentCode: parentCode,
          });
          break;
        case email:
          this.setState({
            isEmailVerified: email,
            isLoading: false,
            parentCode: parentCode,
          });
          break;
        default:
          this.setState({
            isLoading: false,
            parentCode: parentCode,
          });
      }
    });
  }

  startCountdown = (key: 'emailRetrySec' | 'phoneRetrySec', sec: number) => {
    this.setState({ [key]: sec } as any);

    const interval = setInterval(() => {
      this.setState(prev => {
        const v = (prev as any)[key];
        if (!v || v <= 1) {
          clearInterval(interval);
          return { [key]: undefined } as any;
        }
        return { [key]: v - 1 } as any;
      });
    }, 1000);
  };

  startCountdownFromNextResend = (key: 'emailRetrySec' | 'phoneRetrySec', nextResendAt?: number) => {
    if (!nextResendAt) return;

    const sec = Math.max(1, Math.ceil((nextResendAt - Date.now()) / 1000));
    this.startCountdown(key, sec);
  };

  onEmailTokenChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value;
    this.setState({ emailToken: value });
  };

  onPhoneTokenChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value;
    this.setState({ phoneToken: value });
  };

  onVerifyEmailClick = () => {
    const { customer } = this.props;
    const { emailToken } = this.state;

    verifyEmail(customer, emailToken)
      .then(res => {
        const payload = (res as any)?.data ?? res;
        if (payload?.isError) {
          this.openInfoModal('Error', this.getUserFriendlyError(payload, 'Could not verify email.'));
          this.setState({ isEmailVerified: false });
          return;
        }
        this.setState({ isEmailVerified: Boolean(payload?.confirmed) });
      })
      .catch(err => {
        const serverPayload = err?.response?.data;
        this.openInfoModal('Error', this.getUserFriendlyError(serverPayload, 'Could not verify email.'));
        this.setState({ isEmailVerified: false });
      });
  };

  onVerifyPhoneClick = () => {
    const { customer } = this.props;
    const { phoneToken } = this.state;

    verifyPhone(customer, phoneToken)
      .then(res => {
        const payload = (res as any)?.data ?? res;

        if (payload?.isError) {
          this.openInfoModal('Error', this.getUserFriendlyError(payload, 'Could not verify phone.'));
          this.setState({ isPhoneVerified: false });
          return;
        }

        this.setState({ isPhoneVerified: Boolean(payload?.confirmed) });
      })
      .catch(err => {
        const serverPayload = err?.response?.data;
        this.openInfoModal('Error', this.getUserFriendlyError(serverPayload, 'Could not verify phone.'));
        this.setState({ isPhoneVerified: false });
      });
  };

  getUserFriendlyError(payload: any, fallback: string) {
    const code = payload?.code;
    const msg = payload?.message;
    const retry = payload?.retryAfterSec;

    if (code === 'RATE_LIMIT') {
      return msg || `Too many attempts. Please wait ${retry ?? 30} seconds and try again.`;
    }

    if (code === 'TOO_SOON') {
      return msg || `Please wait ${retry ?? 30} seconds before trying again.`;
    }

    if (code === 'AUTO_BLOCKED') {
      return msg || `Temporarily blocked. Please try again later.`;
    }

    if (code === 'EMAIL_NOT_CONFIRMED') {
      return msg || 'Please verify your email first.';
    }

    if (code === 'FORBIDDEN') {
      return msg || 'Not allowed.';
    }

    return msg || fallback;
  }

  ensureEmailVerifiedForSms = (): boolean => {
    const { isEmailVerified } = this.state;

    if (isEmailVerified !== true) {
      this.openInfoModal('Info', 'To receive an SMS verification code, please verify your email first.');
      return false;
    }

    return true;
  };

  openInfoModal = (title: string, body: string) => {
    this.setState({
      isInfoModalOpen: true,
      infoModalTitle: title,
      infoModalBody: body,
    });
  };

  getRetryAfterSec = (errOrRes: any): number | undefined => {
    const headers = errOrRes?.response?.headers || errOrRes?.headers;

    const retryHeader = headers?.['retry-after'];
    const retry = Number(retryHeader);
    if (Number.isFinite(retry) && retry > 0) return Math.floor(retry);

    const payload = errOrRes?.response?.data ?? errOrRes?.data ?? errOrRes;
    const retry2 = Number(payload?.retryAfterSec);
    if (Number.isFinite(retry2) && retry2 > 0) return Math.floor(retry2);

    return undefined;
  };

  getResendMessage = (retrySec?: number) => {
    if (retrySec) {
      return `Please wait ${retrySec} seconds before requesting another code.`;
    }
    return 'Could not resend the verification code.';
  };

  onStartClick = () => {
    const { history, isRegistrationFromOneTimeCode, customer, updateIsCustomerVerified } = this.props;

    updateIsCustomerVerified();

    const state = { ...(history.location.state as any) };

    const searchParams = new URLSearchParams(history.location.search);
    const schoolCode = searchParams.get('schoolCode') || state.schoolCode;
    const isParentInvite = searchParams.get('isParentInvite');

    delete state.phone;
    delete state.email;
    history.replace({ ...history.location, state });

    const isParentCodeExist = typeof schoolCode !== 'undefined' && schoolCode !== '';
    const isBasketExist = typeof customer.basketId !== 'undefined' && customer.basketId !== '';

    const queryParams = new URLSearchParams();
    if (isParentCodeExist) queryParams.append('schoolCode', schoolCode);
    if (isParentInvite === 'true') queryParams.append('isParentInvite', 'true');

    if (isParentCodeExist || isParentInvite === 'true') {
      history.push({ pathname: '/customer/profile', search: queryParams.toString() });
    } else if (isRegistrationFromOneTimeCode && isBasketExist) {
      history.push('/customer/basket');
    } else {
      history.push('/customer');
    }
  };

  onChangeEmailClick = () => {
    this.setState({
      isEmailChangeModalOpen: true,
    });
  };

  onChangePhoneClick = () => {
    this.setState({
      isPhoneChangeModalOpen: true,
    });
  };

  onPhoneChangeFormCancelClick = () => {
    this.setState({
      isPhoneChangeModalOpen: false,
    });
  };

  onEmailChangeFormCancelClick = () => {
    this.setState({
      isEmailChangeModalOpen: false,
    });
  };

  onResendEmailCodeClick = () => {
    const { customer } = this.props;

    this.setState({ isLoading: true });

    resendEmailCode(customer)
      .then(res => {
        const payload = (res as any)?.data ?? res;

        if (payload?.isError) {
          const retry = this.getRetryAfterSec(res);
          if (retry) {
            this.startCountdown('emailRetrySec', retry);
          }
          this.openInfoModal(retry ? 'Info' : 'Error', this.getResendMessage(retry));
        } else {
          this.openInfoModal('Info', 'Verification code sent.');

          const next = payload?.nextResendAt;
          this.startCountdownFromNextResend('emailRetrySec', next);
        }

        this.setState({ isLoading: false });
      })
      .catch(err => {
        const retry = this.getRetryAfterSec(err);
        if (retry) {
          this.startCountdown('emailRetrySec', retry); // <-- FIX
        }
        this.openInfoModal(retry ? 'Info' : 'Error', this.getResendMessage(retry));
        this.setState({ isLoading: false });
      });
  };

  onResendPhoneCodeClick = () => {
    const { customer } = this.props;

    if (!this.ensureEmailVerifiedForSms()) return;

    this.setState({ isLoading: true });

    resendPhoneCode(customer)
      .then(res => {
        const payload = (res as any)?.data ?? res;

        if (payload?.isError) {
          const retry = this.getRetryAfterSec(res);
          if (retry) this.startCountdown('phoneRetrySec', retry);
          this.openInfoModal(retry ? 'Info' : 'Error', this.getResendMessage(retry));
        } else {
          this.openInfoModal('Info', 'Verification code sent.');

          const next = payload?.nextResendAt;
          this.startCountdownFromNextResend('phoneRetrySec', next);
        }

        this.setState({ isLoading: false });
      })
      .catch(err => {
        const retry = this.getRetryAfterSec(err);
        if (retry) this.startCountdown('phoneRetrySec', retry);
        this.openInfoModal(retry ? 'Info' : 'Error', this.getResendMessage(retry));
        this.setState({ isLoading: false });
      });
  };

  onEmailChangeFormSubmit = data => {
    const { customer } = this.props;
    this.setState({ isLoading: true });

    changeEmail(customer, data.email)
      .then(res => {
        const payload = (res as any)?.data ?? res;

        if (payload?.isError) {
          this.openInfoModal('Error', this.getUserFriendlyError(payload, 'Could not change email.'));
          this.setState({ isLoading: false });
          return;
        }

        this.setState({
          isLoading: false,
          isEmailChangeModalOpen: false,
        });

        this.openInfoModal('Info', 'Email updated. We sent a new verification code.');

        const { history } = this.props;
        const state = { ...(history.location.state as any) };
        state.email = data.email;
        history.replace({ ...history.location, state });
      })
      .catch(err => {
        const serverPayload = err?.response?.data;
        this.openInfoModal('Error', this.getUserFriendlyError(serverPayload, 'Could not change email.'));
        this.setState({ isLoading: false });
      });
  };

  onPhoneChangeFormSubmit = data => {
    const { customer } = this.props;
    this.setState({ isLoading: true });

    const { phone, phoneCode } = data;
    const phoneWithoutFirstZero = getPhoneWithoutFirstZero(phone);
    const phoneToPost = `${phoneCode}${phoneWithoutFirstZero}`;

    changePhone(customer, phoneToPost)
      .then(res => {
        const payload = (res as any)?.data ?? res;

        if (payload?.isError) {
          this.openInfoModal('Error', this.getUserFriendlyError(payload, 'Could not change phone.'));
          this.setState({ isLoading: false });
          return;
        }

        this.setState({
          isLoading: false,
          isPhoneChangeModalOpen: false,
        });

        const { isEmailVerified } = this.state;
        if (isEmailVerified === true) {
          this.openInfoModal('Info', 'Phone updated. We sent a new verification code.');
        } else {
          this.openInfoModal(
            'Info',
            'Phone updated. To receive an SMS verification code, please verify your email first.'
          );
        }

        const { history } = this.props;
        const state = { ...(history.location.state as any) };
        state.phone = phoneWithoutFirstZero;
        state.code = data.phoneCode;
        history.replace({ ...history.location, state });
      })
      .catch(err => {
        const serverPayload = err?.response?.data;
        this.openInfoModal('Error', this.getUserFriendlyError(serverPayload, 'Could not change phone.'));
        this.setState({ isLoading: false });
      });
  };

  renderEmailChangeModal() {
    const { isEmailChangeModalOpen } = this.state;
    return (
      <SimpleModal isOpen={isEmailChangeModalOpen}>
        <EmailChangeForm
          onCancel={this.onEmailChangeFormCancelClick}
          onSubmit={this.onEmailChangeFormSubmit}
          {...this.props}
        />
      </SimpleModal>
    );
  }

  renderPhoneChangeModal() {
    const { isPhoneChangeModalOpen } = this.state;
    return (
      <SimpleModal isOpen={isPhoneChangeModalOpen}>
        <PhoneChangeForm
          onCancel={this.onPhoneChangeFormCancelClick}
          onSubmit={this.onPhoneChangeFormSubmit}
          {...this.props}
        />
      </SimpleModal>
    );
  }

  onOkInfoModalClick = () => {
    this.setState({ isInfoModalOpen: false });
  };

  renderInfoModal() {
    const { isInfoModalOpen, infoModalTitle, infoModalBody } = this.state;

    return (
      <SimpleModal
        isOpen={isInfoModalOpen}
        title={infoModalTitle}
        body={infoModalBody}
        buttonCancelText={'Ok'}
        onCloseClick={this.onOkInfoModalClick}
      />
    );
  }

  render() {
    const {
      emailToken,
      phoneToken,
      isEmailVerified,
      isPhoneVerified,
      isLoading,
      isEmailChangeModalOpen,
      isPhoneChangeModalOpen,
      isInfoModalOpen,
    } = this.state;

    const { history } = this.props;
    const state = { ...(history.location.state as any) };

    const { email, phone, code } = state;

    if (isLoading) {
      return <Loader />;
    }

    const classes = isEmailChangeModalOpen || isPhoneChangeModalOpen || isInfoModalOpen ? 'mt-3 modal-open' : 'mt-3';

    return (
      <div className={classes}>
        {/*Place for render modal windows*/}
        {this.renderEmailChangeModal()}
        {this.renderPhoneChangeModal()}
        {this.renderInfoModal()}
        <div className="container">
          <div className="row">
            <div className="col-xl-6 offset-xl-3">
              <h1 className={'mt-3 mb-3'}>Verification form</h1>
              <div className={'mt-3 mb-3'}>
                <label>Email verification</label>
                <input
                  type="text"
                  className="form-control mb-3"
                  placeholder="Enter code here"
                  value={emailToken}
                  onChange={this.onEmailTokenChange}
                  disabled={isEmailVerified}
                />
                <p>We have sent your verification code to {email}</p>
                {isEmailVerified === false && (
                  <div className="alert alert-danger">
                    We couldn’t verify that code. Please try again, or request a new one.
                  </div>
                )}
                {isEmailVerified && <div className="alert alert-success">Verified</div>}
                <Button disabled={isEmailVerified} text={'Verify'} onClick={this.onVerifyEmailClick} />
                <Button
                  disabled={isEmailVerified}
                  text={'Change email'}
                  onClick={this.onChangeEmailClick}
                  customClass={'btn-light ml-3'}
                />
                <Button
                  disabled={isEmailVerified || !!this.state.emailRetrySec}
                  text={
                    this.state.emailRetrySec ? `Resend email code (${this.state.emailRetrySec}s)` : 'Resend email code'
                  }
                  onClick={this.onResendEmailCodeClick}
                  customClass={'btn-light ml-3'}
                />
              </div>
              <div className={'mt-3 mb-3'}>
                <label>Phone verification</label>
                <input
                  type="text"
                  className="form-control mb-3"
                  placeholder="Enter code here"
                  value={phoneToken}
                  onChange={this.onPhoneTokenChange}
                  disabled={isPhoneVerified}
                />
                <p>
                  We have sent your verification code to {code} {phone}
                </p>
                {isPhoneVerified === false && (
                  <div className="alert alert-danger">
                    We couldn’t verify that code. Please try again, or request a new one.
                  </div>
                )}
                {isPhoneVerified && <div className="alert alert-success">Verified</div>}
                <Button disabled={isPhoneVerified} text={'Verify'} onClick={this.onVerifyPhoneClick} />
                <Button
                  disabled={isPhoneVerified}
                  text={'Change phone'}
                  onClick={this.onChangePhoneClick}
                  customClass={'btn-light ml-3'}
                />
                <Button
                  disabled={isPhoneVerified || !!this.state.phoneRetrySec}
                  text={
                    this.state.phoneRetrySec ? `Resend phone code (${this.state.phoneRetrySec}s)` : 'Resend phone code'
                  }
                  onClick={this.onResendPhoneCodeClick}
                  customClass={'btn-light ml-3'}
                />
              </div>

              {isEmailVerified && isPhoneVerified && <Button text={'Start'} onClick={this.onStartClick} />}
            </div>
          </div>
        </div>
      </div>
    );
  }
}
